import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CustomerAccount } from '@inyova/models';

import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { CriteriumData, StrategyTopicsIds, TopicData } from '@shared/models/Strategy';
import { LanguageService } from '@shared/services/language.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-strategy-topics',
  templateUrl: './strategy-topics.component.html',
  styleUrls: ['./strategy-topics.component.scss'],
  standalone: false
})
export class StrategyTopicsComponent implements OnInit, OnDestroy {
  @Input() is3aPillar: boolean;
  tuvLanguage: string;

  isLoading$: Observable<boolean>;
  vm$: Observable<{
    currentAccount: CustomerAccount;
    handTopics: TopicData[];
    footTopics: TopicData[];
    criteria: CriteriumData[];
    originalIds: StrategyTopicsIds;
    selectedIds: StrategyTopicsIds;
  }>;

  protected readonly onDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private trackingService: TrackingService,
    private languageService: LanguageService
  ) {
    this.store
      .pipe(
        select(fromRoot.selectLanguageChange),
        filter((isLanguageChange) => isLanguageChange === true),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.store.dispatch(StrategyActions.getTopicsData());
        this.store.dispatch(StrategyActions.getCriteriaData());
        this.store.dispatch(AppRootActions.setIsLanguageChange({ change: false }));
      });
  }

  ngOnInit(): void {
    this.vm$ = this.store.select(fromStrategy.selectStrategyTopicsView);
    this.isLoading$ = this.store.select(fromStrategy.selectTopicsLoader);

    this.tuvLanguage = this.languageService.getRedirectLanguage();
    this.trackingService.trackView('/topics');
  }

  wasChanged(originalIds: StrategyTopicsIds, selectedIds: StrategyTopicsIds): boolean {
    return (
      JSON.stringify(originalIds) !==
      JSON.stringify({
        selectedHandTopics: selectedIds.selectedHandTopics.sort((a, b) => a - b),
        selectedFootTopics: selectedIds.selectedFootTopics.sort((a, b) => a - b),
        selectedCriteria: selectedIds.selectedCriteria.sort((a, b) => a - b)
      })
    );
  }

  cancelChanges(): void {
    this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: 5 }));
  }

  discardChanges(discarded: boolean, originalIds: StrategyTopicsIds, selectedIds: StrategyTopicsIds) {
    if (!discarded) return;
    selectedIds.selectedCriteria = [...originalIds.selectedCriteria];
    selectedIds.selectedFootTopics = [...originalIds.selectedFootTopics];
    selectedIds.selectedHandTopics = [...originalIds.selectedHandTopics];
  }

  selectCard($event: Event, array: number[], newId: number, originalIds: StrategyTopicsIds, selectedIds: StrategyTopicsIds): void {
    // don't add if the event target is i icon
    if (($event.target as Element).className === 'icon') {
      return;
    }

    if (this.isSelectedCard(array, newId)) {
      array.splice(array.indexOf(newId), 1);
      this.trackingService.trackActivity(`[Card] Topic ID ${newId} unselected`);
    } else {
      array.push(newId);

      this.trackingService.trackActivity(`[Card] Topic ID ${newId} selected`);
    }

    this.store.dispatch(
      StrategyActions.setStrategyWasChanged({
        strategyWasChanged: this.wasChanged(originalIds, selectedIds),
        modalOpened: false,
        redirectUrl: ''
      })
    );
  }

  isSelectedCard(array: number[], id: number): boolean {
    return array.indexOf(id) !== -1;
  }

  send(continueToPortfolio: boolean, selectedIds: StrategyTopicsIds): void {
    const payload = {
      criteria: selectedIds.selectedCriteria,
      topics: selectedIds.selectedHandTopics.concat(selectedIds.selectedFootTopics)
    };

    this.store.dispatch(StrategyActions.updateTopics({ continueToPortfolio, updateObject: payload }));
  }

  toggleGuaranteeModal(): void {
    this.store.dispatch(StrategyActions.setGuaranteeVisibility({ value: true }));
    this.trackingService.trackActivity(`[Modal] Guarantee`);
  }

  trackActivity(activity: string): void {
    this.trackingService.trackActivity(activity);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
